import {
	CONSENT_COOKIE_MAX_AGE_SECONDS,
	CONSENT_COOKIE_NAME,
	CONSENT_COOKIE_VERSION,
	type UsercentricsDecision,
} from './consent-cookie';
/**
 * The slice of the CMP's browser API the Worldcoo apps use.
 *
 * `window.__ucCmp` is the v3 native surface; `window.UC_UI` is the compatibility surface that carries
 * the same `showSecondLayer`. Both are present at runtime, and neither is fully covered by the public
 * Control UI docs — `changeLanguage` and `getActiveLanguage` in particular are undocumented but real,
 * verified against the loaded CMP. Declared here rather than cast at the call site so nothing needs `as`.
 */
interface UsercentricsCmp {
	readonly getActiveLanguage: () => Promise<string>;
	readonly changeLanguage: (language: string) => Promise<void>;
	readonly showFirstLayer: () => Promise<void>;
	readonly showSecondLayer: () => Promise<void>;
	readonly closeCmp: () => Promise<void>;
	readonly isConsentRequired: () => Promise<boolean>;
	readonly getConsentDetails: () => Promise<UsercentricsConsentDetails>;
}

/** Only the part of `getConsentDetails()` this app reads: per-service consent, keyed by template id. */
interface UsercentricsConsentDetails {
	readonly services?: Record<string, { readonly name?: string; readonly consent?: { readonly given?: boolean } }>;
}

/**
 * Window event the CMP dispatches whenever consent changes, so a gated embed can appear the moment the
 * donor accepts instead of waiting for a navigation. Verified against the loaded CMP, which fires it
 * alongside `UC_CONSENT` and `UC_GCM_UPDATE`; this is the one that names the thing we care about.
 */
const CMP_CONSENT_CHANGED_EVENT = 'UC_CONSENT_CHANGED';

declare global {
	interface Window {
		__ucCmp?: UsercentricsCmp;
		UC_UI?: Pick<UsercentricsCmp, 'showSecondLayer'>;
		/**
		 * The CMP's own opt-out from opening its banner by itself, read by the loader at init — the
		 * documented way to keep a consent banner off a page that exists to be read. See
		 * {@link loadUsercentrics}.
		 */
		UC_UI_SUPPRESS_CMP_DISPLAY?: boolean;
	}
}

/** Whether {@link loadUsercentrics} has already put the loader in the document, per browser session. */
let cmpLoaderRequested = false;

/**
 * Whether this module is the reason no banner is on screen — either the loader was told not to open one
 * or {@link syncUsercentricsBannerVisibility} closed it. Only then may it be re-opened on the way out; a
 * banner the CMP decided against on its own is none of our business.
 */
let bannerWithheld = false;

/**
 * Injects the Usercentrics CMP loader — the cookie consent banner — imperatively and only after the
 * document has hydrated.
 *
 * It is deliberately NOT declared in the framework's `head()`, for exactly the reason the Google
 * Analytics tag is not (see `loadGtag` and {@link bootstrapHeadScripts}): a `<script>` with a `src` and
 * no children is the one head element React runs its leftover-children check on, so if anything has
 * shifted the head by a position React pairs it with an inline script whose text node is then an
 * unconsumed child — React #418 `args[]=HTML`, and the whole tree is regenerated. That shift reached
 * production on five builds, caused by a third party inserting a script into the head before hydration.
 *
 * A CMP is the worst possible candidate for a head entry on that count, because injecting scripts into
 * the head ahead of everything else is its entire job. Loading it from here keeps the hydrated head
 * inline-only, so neither the loader nor anything it goes on to inject can cost more than the banner.
 *
 * `locale` is the language to open the banner in. Pass the one the page itself resolved: left to its
 * own devices the CMP opens in the configuration's first language — Spanish for `cOGktGJI3HbRT3` — for
 * everyone. Verified against the loaded CMP on the donors app, which is a client-rendered SPA and so has
 * already rewritten `<html lang>` to the donor's language before this runs: the banner still came up in
 * Spanish for an `en` browser, and `navigator.language` did not move it either. Neither of the implicit
 * signals is ours to rely on, so the app states it. {@link syncUsercentricsLanguage} then keeps it in step
 * with later switches; this is only about the language it first appears in.
 *
 * `suppressBanner` keeps the CMP from opening its banner on arrival, for a page whose whole purpose is
 * to be read — see {@link syncUsercentricsBannerVisibility}. It has to be decided here, before the loader
 * runs, because that is the only point at which the banner can be prevented rather than removed after the
 * fact: `window.UC_UI_SUPPRESS_CMP_DISPLAY` is read once at init, and setting it later does nothing.
 *
 * Safe to call more than once, and a no-op without a settings id or a document.
 */
export const loadUsercentrics = (settingsId: string, draft: boolean, locale?: string, suppressBanner = false): void => {
	if (typeof document === 'undefined' || !settingsId || cmpLoaderRequested) return;
	cmpLoaderRequested = true;
	// Before the element is appended, so the flag is already in place when the loader reads it.
	if (suppressBanner) {
		window.UC_UI_SUPPRESS_CMP_DISPLAY = true;
		bannerWithheld = true;
	}
	const script = document.createElement('script');
	// The loader locates its own tag by this id to read the data attributes below, so both the id and the
	// attributes have to be set before the element is appended and the script starts running.
	script.id = 'usercentrics-cmp';
	script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';
	script.async = true;
	script.dataset.settingsId = settingsId;
	// A locale the configuration does not ship falls back to its default, the same silent no-op
	// `syncUsercentricsLanguage` documents — so no allowlist here either, for the same reason.
	if (locale) script.dataset.language = locale;
	// `data-draft` serves the unpublished configuration, so a change to the consent model can be tried on
	// a portal before it reaches donors. Set on every stage but `pro`, and omitted entirely there rather
	// than written as `data-draft="false"`: production matches the documented published-config tag exactly,
	// so it cannot depend on how the loader happens to coerce that value.
	if (draft) script.dataset.draft = 'true';
	document.head.appendChild(script);
};

/**
 * Points the CMP's banner at a language, so it follows the page instead of staying in whichever
 * language the document first loaded in.
 *
 * This is needed because switching locale here never reloads the document: `useTranslation`'s
 * `changeLanguage` pushes a new path through the router. The CMP reads `<html lang>` once, at init, so
 * without this the banner keeps rendering its original language while every other string on the page
 * changes — visible whenever a donor switches language before answering it, and again every time they
 * reopen the settings layer afterwards.
 *
 * A locale the configuration does not ship is a silent no-op inside the CMP — verified against `eu`,
 * which portals serves and the CMP does not: no throw, banner left intact. So this deliberately keeps
 * no allowlist of its own, which would only drift from the Admin Interface.
 *
 * Safe to call before the CMP has loaded (it does nothing, and the CMP reads `<html lang>` itself when
 * it arrives) and safe to call redundantly.
 */
export const syncUsercentricsLanguage = async (locale: string): Promise<void> => {
	const cmp = window.__ucCmp;
	if (!cmp || !locale) return;
	// Skip the redundant call: this runs on every locale-bearing render, and re-applying the active
	// language makes the CMP re-render its banner for nothing.
	if ((await cmp.getActiveLanguage()) === locale) return;
	await cmp.changeLanguage(locale);
};

/**
 * Keeps the consent banner off the pages that exist to be read, and brings it back on the way out.
 *
 * The banner opens as a centred modal with a backdrop, so on the legal notice, the privacy policy and the
 * cookie policy it lands on top of the very text the donor came to read — including when they got there
 * from the banner's own links to those pages, which is the one journey where covering the page is certain
 * to happen. Suppressing it there costs nothing: nothing is consented to in the meantime, and the banner is
 * due again the moment the donor leaves for a page that actually processes their data. Which pages those
 * are is the caller's to decide; in portals see `useBannerFreeRoute`.
 *
 * Two mechanisms, because one page view can start on either side of the CMP's arrival:
 *
 * - a page ENTERED with the banner already up (a client-side navigation, which is how a footer link is
 *   followed) is only reachable by closing it, which is what this does. `closeCmp` dismisses whichever
 *   layer is open without recording anything — verified against the loaded CMP, where `isConsentRequired`
 *   still answered `true` afterwards and the stored record was untouched.
 * - a page LOADED directly cannot be handled from here without the banner flashing first, so the loader
 *   is told not to open it at all. See `suppressBanner` in {@link loadUsercentrics}.
 *
 * Leaving such a page re-opens the banner, gated on the CMP's own `isConsentRequired` so that a donor who
 * has already answered — or one in a region where no banner is due — is not asked again.
 *
 * Deliberately only the FIRST layer. The settings layer stays available on these pages, because the
 * footer's "Privacy settings" control is on them like everywhere else (see `PrivacySettingsLink`), and on
 * the privacy policy and the cookie policy in particular it is the control the text itself points at — so
 * a donor who came to read what they are agreeing to can still answer, in the layer that lets them choose
 * per service rather than in the one that only offers all or nothing.
 *
 * Re-opening is conditional on this module having taken the banner away in the first place, so the
 * ordinary case — a visit that never touches a legal page — is left exactly as the CMP arranges it, and
 * nothing here can turn into a banner the CMP had not decided to show.
 *
 * Safe to call before the CMP has loaded, where it does nothing: the initial state is then the loader's
 * business, not ours.
 */
export const syncUsercentricsBannerVisibility = async (suppressed: boolean): Promise<void> => {
	const cmp = window.__ucCmp;
	if (!cmp) return;
	if (suppressed) {
		bannerWithheld = true;
		await cmp.closeCmp();
		return;
	}
	if (!bannerWithheld) return;
	bannerWithheld = false;
	if (!(await cmp.isConsentRequired())) return;
	await cmp.showFirstLayer();
};

/**
 * Whether the donor has consented to a named data-processing service.
 *
 * Matched on the service's name as configured in the Admin Interface (the CMP returns it unlocalised).
 * Anything unexpected answers `false`: no CMP yet, the service absent from the configuration, a rename.
 * Failing closed is the whole point — a caller uses this to decide whether to contact a third party, so
 * "don't know" has to mean "don't".
 */
export const hasUsercentricsServiceConsent = async (serviceName: string): Promise<boolean> => {
	const cmp = window.__ucCmp;
	if (!cmp) return false;
	const details = await cmp.getConsentDetails();
	const services = Object.values(details.services ?? {});
	return services.some((service) => service.name === serviceName && service.consent?.given === true);
};

/** Key the CMP persists its decision under, readable long before the CMP itself has booted. */
const CMP_STORAGE_KEY = 'ucData';

/**
 * The consent flag on one service entry, or `undefined` when the entry does not carry one we recognise.
 *
 * Two shapes are in play across the CMP's own surfaces: the stored `ucData` record holds a bare boolean,
 * while `getConsentDetails()` nests the same fact under `given` (see {@link UsercentricsConsentDetails}).
 * Reading both here means a change of storage format on Usercentrics' side degrades to "no stored answer",
 * which costs a second's wait for the CMP, instead of to "denied", which is a wrong answer we would then
 * persist. Callers must treat `undefined` as "ask the CMP", never as a refusal.
 */
const readServiceConsentFlag = (service: object): boolean | undefined => {
	if (!('consent' in service)) return undefined;
	const { consent } = service;
	if (typeof consent === 'boolean') return consent;
	if (typeof consent === 'object' && consent !== null && 'given' in consent && typeof consent.given === 'boolean') {
		return consent.given;
	}
	return undefined;
};

/**
 * Reads the donor's stored decision for a named service straight out of the CMP's own storage.
 *
 * This exists for speed, and the speed is visible: the CMP script is only requested after hydration, so
 * asking the loaded CMP (see {@link hasUsercentricsServiceConsent}) does not answer for roughly a second.
 * Gated content therefore sat in its "not decided" state long enough to look like a flash — a donor who had
 * already accepted a video watched the poster sit there and then get replaced by the player. Reading the
 * same value synchronously resolves it on the first frame instead.
 *
 * Returns `undefined` when the answer cannot be established — no storage, unparseable, service absent — so
 * the caller falls back to asking the CMP rather than assuming. It is never more stale than the CMP itself:
 * this is the very record the CMP restores from.
 */
export const readStoredUsercentricsServiceConsent = (serviceName: string): boolean | undefined => {
	const decision = readStoredUsercentricsDecision();
	if (!decision) return undefined;
	return decision.granted.includes(serviceName) ? true : decision.denied.includes(serviceName) ? false : undefined;
};

/**
 * The donor's whole stored decision, by service name, straight out of the CMP's own storage.
 *
 * Returns `undefined` when there is nothing to read — first visit, storage blocked, or a shape this does
 * not recognise. That is deliberately distinct from "decided, and denied everything": callers must be able
 * to tell "no answer yet" from "the answer is no".
 */
export const readStoredUsercentricsDecision = (): UsercentricsDecision | undefined => {
	if (typeof localStorage === 'undefined') return undefined;
	let raw: string | null = null;
	try {
		raw = localStorage.getItem(CMP_STORAGE_KEY);
	} catch {
		// Storage can throw outright when the browser blocks it (Safari private mode, embedded contexts).
		return undefined;
	}
	if (!raw) return undefined;

	try {
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null || !('consent' in parsed)) return undefined;
		const { consent } = parsed;
		if (typeof consent !== 'object' || consent === null || !('services' in consent)) return undefined;
		const { services } = consent;
		if (typeof services !== 'object' || services === null) return undefined;

		const granted: string[] = [];
		const denied: string[] = [];
		for (const service of Object.values(services)) {
			if (typeof service !== 'object' || service === null) continue;
			if (!('name' in service) || typeof service.name !== 'string') continue;
			const given = readServiceConsentFlag(service);
			// Only an explicit answer is recorded. Anything else is left out of BOTH arrays rather than
			// treated as a refusal: this record is mirrored verbatim into `wc-consent` and read back by the
			// server, so guessing "denied" for a shape we failed to read would server-render a hard
			// click-to-load placeholder to a donor who had in fact accepted — and it would look like a
			// deliberate answer rather than a failed read, so nothing downstream would go and ask the CMP.
			if (given === true) granted.push(service.name);
			else if (given === false) denied.push(service.name);
		}
		if (granted.length === 0 && denied.length === 0) return undefined;
		return { granted, denied };
	} catch {
		// A shape we do not recognise is not an answer. Let the caller ask the CMP.
		return undefined;
	}
};

/**
 * Re-derives the mirror cookie from the CMP's stored record.
 *
 * Call on load and on every consent change. Clears the cookie when there is no readable record, so the
 * server falls back to "not decided" rather than trusting something we can no longer confirm.
 */
export const syncConsentCookie = (): void => {
	if (typeof document === 'undefined') return;
	const secure = location.protocol === 'https:' ? '; Secure' : '';
	const decision = readStoredUsercentricsDecision();
	if (!decision) {
		document.cookie = `${CONSENT_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
		return;
	}
	const value = encodeURIComponent(
		JSON.stringify({ v: CONSENT_COOKIE_VERSION, g: decision.granted, d: decision.denied }),
	);
	document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
};

/**
 * Name of the Google Analytics service in the Usercentrics configuration, for the GATE that decides whether
 * gtag.js may be loaded at all. Shared by both donor-facing apps because both point at the same
 * configuration (`cOGktGJI3HbRT3`), where it is registered under the Functional category.
 *
 * Exact, and must stay exact, the way `YOUTUBE_SERVICE_NAME` is: a gate has to fail closed, and an exact
 * name that stops matching resolves to `denied`, so the tag is never loaded. Note the cost of that failure
 * is not symmetrical with YouTube's — a mismatched YouTube name degrades one embed to click-to-load, while
 * a mismatched name here silently stops ALL analytics collection. It is still the right direction to fail
 * in, and {@link clearCookiesForRefusedServices} reports a configuration that names no analytics service at
 * all, so a rename does not pass unnoticed.
 */
export const ANALYTICS_SERVICE_NAME = 'Google Analytics 4';

/**
 * Matches the Google Analytics service in the CMP configuration, and the cookies it writes.
 *
 * A pattern rather than the exact configured name (the way `YOUTUBE_SERVICE_NAME` is written) because the
 * consequence of missing here is the opposite one. A name that stops matching for a GATE degrades to
 * "denied" and shows a click-to-load prompt — annoying, never leaky. A name that stops matching for a
 * CLEANUP degrades to never running, which looks like compliance and is not, so this is deliberately loose
 * enough to survive "Google Analytics" being renamed to "Google Analytics 4" or similar. If it matches
 * nothing at all, that is reported rather than silently assumed.
 */
const ANALYTICS_SERVICE_PATTERN = /google analytics/i;
const ANALYTICS_COOKIE_PATTERN = /^_ga(_|$)/;

/**
 * Expires a first-party cookie across every domain scope it could have been written at.
 *
 * A delete only takes effect when the `Domain` matches the one the cookie was set with, and the browser
 * never tells us what that was — `document.cookie` hands back names and values, nothing else. gtag.js
 * writes `_ga` on the highest registrable domain it can reach, which on a portal is `worldcoo.com` and not
 * the host being served, so expiring it host-scoped alone would silently do nothing. Walking from the exact
 * host up to the registrable domain covers both; the scopes that do not match are no-ops.
 */
const expireFirstPartyCookie = (name: string): void => {
	const expiry = `${encodeURIComponent(name)}=; Max-Age=0; Path=/`;
	document.cookie = expiry;
	const labels = location.hostname.split('.');
	// Stops before the last label: a cookie cannot be set on a public suffix, and trying is harmless noise.
	for (let i = 0; i < labels.length - 1; i++) {
		const domain = labels.slice(i).join('.');
		document.cookie = `${expiry}; Domain=${domain}`;
		document.cookie = `${expiry}; Domain=.${domain}`;
	}
};

/**
 * Deletes the analytics cookies when the donor has not consented to analytics.
 *
 * Consent Mode is not enough on its own, and this is the gap it leaves: setting `analytics_storage` to
 * `denied` stops gtag.js READING or WRITING `_ga`/`_ga_*` and puts it on cookieless pings, but it never
 * removes what a previous `granted` already wrote. A donor who accepted, then withdrew, would otherwise
 * keep a two-year client id on their device — storage they have explicitly refused, which is the thing
 * withdrawal is supposed to end.
 *
 * Only analytics. The other entries in the cookie policy are either not consent-gated at all (reCAPTCHA,
 * Adyen, the language and consent-mirror cookies, all declared necessary) or belong to another origin and
 * cannot be touched from here (YouTube's — see `video-embed.tsx`, where unmounting the player is the whole
 * of what we can do).
 *
 * Not a duplicate of something the CMP already does, checked rather than assumed: the published
 * configuration both apps share (`cOGktGJI3HbRT3`, readable at
 * `api.usercentrics.eu/settings/<id>/latest/en.json`) reports `trackerDeclarationEnabled: false`, carries no
 * device-storage declaration on any service, and has auto-blocking off. A CMP can only delete cookies it
 * has been told about, and this one has been told about none. If tracker declaration is ever switched on in
 * the Admin Interface, check whether this became redundant.
 *
 * Call on load and on every consent change, alongside {@link syncConsentCookie}. Absence of a stored record
 * counts as "not granted": nothing was consented to, so nothing may remain.
 */
export const clearCookiesForRefusedServices = (): void => {
	if (typeof document === 'undefined') return;
	const decision = readStoredUsercentricsDecision();

	if (decision && ![...decision.granted, ...decision.denied].some((name) => ANALYTICS_SERVICE_PATTERN.test(name))) {
		// A decision that names no analytics service means the pattern above no longer describes the CMP
		// configuration. Bail rather than delete: from here we cannot tell "analytics was refused" from
		// "analytics was accepted under a name we did not recognise", and wiping a consenting donor's client
		// id on every consent change would be a silent, permanent hole in measurement.
		console.error(new Error('No Usercentrics service matched the analytics pattern; cookie cleanup skipped'));
		return;
	}
	if (decision?.granted.some((name) => ANALYTICS_SERVICE_PATTERN.test(name))) return;

	for (const entry of document.cookie.split('; ')) {
		const name = entry.slice(0, entry.indexOf('='));
		if (name && ANALYTICS_COOKIE_PATTERN.test(name)) expireFirstPartyCookie(name);
	}
};

/** Subscribes to CMP consent changes. Returns an unsubscribe function; a no-op without a document. */
export const onUsercentricsConsentChanged = (listener: () => void): (() => void) => {
	if (typeof window === 'undefined') return () => undefined;
	window.addEventListener(CMP_CONSENT_CHANGED_EVENT, listener);
	return () => window.removeEventListener(CMP_CONSENT_CHANGED_EVENT, listener);
};

/** How long we wait for the CMP to appear, and how often we look, before settling for without it. */
const CMP_READY_TIMEOUT_MS = 15_000;
const CMP_READY_POLL_MS = 200;

/**
 * The one poll for the CMP's arrival, shared by every caller of {@link onUsercentricsReady}.
 *
 * A portal home page renders a gated embed per campaign card, and each one asks the same question. Giving
 * each its own interval meant N timers doing an identical property read on the same tick, when they all
 * learn the same fact at the same moment. One poll answers all of them.
 *
 * Cleared once it settles, so a component mounting later starts a fresh wait rather than inheriting a
 * `false` that was decided before the CMP happened to arrive.
 */
let cmpReadyPromise: Promise<boolean> | undefined;

const waitForCmp = (): Promise<boolean> => {
	if (window.__ucCmp) return Promise.resolve(true);
	cmpReadyPromise ??= new Promise<boolean>((resolve) => {
		let waited = 0;
		const timer = window.setInterval(() => {
			waited += CMP_READY_POLL_MS;
			if (window.__ucCmp) {
				window.clearInterval(timer);
				resolve(true);
			} else if (waited >= CMP_READY_TIMEOUT_MS) {
				window.clearInterval(timer);
				resolve(false);
			}
		}, CMP_READY_POLL_MS);
	}).then((available) => {
		cmpReadyPromise = undefined;
		return available;
	});
	return cmpReadyPromise;
};

/**
 * Calls back once the CMP's browser API exists — with `false` if it never turns up.
 *
 * Every consumer needs this because nothing else announces it. {@link loadUsercentrics} is injected
 * from an effect and fetched asynchronously, so a read taken at mount is always too early; and
 * `UC_CONSENT_CHANGED` fires when the donor changes their mind, never for the decision the CMP
 * restores at init. Between the two, a returning donor who had already accepted YouTube was told
 * "denied" for the whole visit and shown the click-to-load prompt on every page.
 *
 * Polls rather than listening for an initialisation event: the events this module uses are the ones
 * verified against the loaded CMP, and an init event is not among them. A bounded poll costs a handful
 * of property reads and cannot be broken by Usercentrics renaming an event.
 *
 * Reports `false` after {@link CMP_READY_TIMEOUT_MS} so a caller can stop waiting: a CMP blocked by an
 * extension, or one deliberately not loaded, never arrives, and leaving a gated embed in its
 * "still asking" state forever is worse than telling the donor it is off.
 *
 * Answers on a microtask even when the CMP is already there, rather than synchronously, because every
 * caller shares {@link waitForCmp}'s single poll. Callers seed themselves from the mirror cookie or the
 * stored record first, so nothing renders differently for the extra tick.
 *
 * Returns a teardown that stops this listener being called. It does not cancel the shared poll, which is
 * bounded anyway and is doing the same work for everyone else.
 */
export const onUsercentricsReady = (listener: (available: boolean) => void): (() => void) => {
	if (typeof window === 'undefined') return () => undefined;
	let active = true;
	void waitForCmp().then((available) => {
		if (active) listener(available);
	});
	return () => {
		active = false;
	};
};

/** Id of the `<style>` this module owns inside the CMP's shadow root. */
const CMP_THEME_STYLE_ID = 'worldcoo-cmp-primary';

/**
 * The CMP's own shadow-root custom properties, repointed at the page's primary colour.
 *
 * These names come from the CMP's own stylesheet (`--color-main-primary: #0045A5`, and siblings for the
 * buttons, links, focus ring and toggles). Overriding them is deliberate in preference to the
 * `updateTheme` API: that API's `ThemeData` shape is not in the public docs, and every shape probed
 * against the live CMP silently reset the theme to a default grey rather than erroring. If Usercentrics
 * ever renames one of these, the declaration simply stops matching and the banner falls back to its
 * configured colours — a cosmetic no-op, not a broken banner.
 */
function cmpThemeCss(primary: string, onPrimary: string): string {
	return (
		'.cmp,.modal-wrapper,.privacyNotice{' +
		`--color-main-primary:${primary};` +
		`--color-links-anchor:${primary};` +
		`--color-focus:${primary};` +
		`--color-buttons-accept-background:${primary};` +
		`--color-buttons-accept-text:${onPrimary};` +
		`--color-buttons-save-background:${primary};` +
		`--color-buttons-save-text:${onPrimary};` +
		`--color-toggle-background-active:${primary};` +
		'}'
	);
}

/**
 * Runs `attach` against the CMP's shadow root once it exists, and hands back one teardown for both the
 * wait and whatever `attach` set up.
 *
 * The wait is bounded by the same timeout as {@link onUsercentricsReady}, and for the same reason: the
 * host may never appear. An extension blocked the loader (`web.cmp.usercentrics.eu` is on EasyPrivacy),
 * the visitor is offline, or this is an embed where `__root.tsx` deliberately never loads the CMP at all.
 * A `document.body` observer with `subtree` runs its callback on every DOM change anywhere in the page,
 * so one left waiting forever is a cost paid for the whole visit by exactly the visitors who will never
 * see a banner at all.
 */
const whenCmpShadowRoot = (attach: (shadow: ShadowRoot) => () => void): (() => void) => {
	let detach: (() => void) | undefined;
	const tryAttach = (): boolean => {
		const shadow = document.getElementById('usercentrics-cmp-ui')?.shadowRoot;
		if (!shadow) return false;
		detach = attach(shadow);
		return true;
	};

	if (tryAttach()) return () => detach?.();

	let giveUp: number | undefined;
	let hostObserver: MutationObserver | undefined;
	const stopWaiting = (): void => {
		if (giveUp !== undefined) window.clearTimeout(giveUp);
		hostObserver?.disconnect();
	};
	hostObserver = new MutationObserver(() => {
		if (tryAttach()) stopWaiting();
	});
	hostObserver.observe(document.body, { childList: true, subtree: true });
	giveUp = window.setTimeout(stopWaiting, CMP_READY_TIMEOUT_MS);
	return () => {
		stopWaiting();
		detach?.();
	};
};

/**
 * Dresses the consent banner in the page's own primary colour instead of the CMP's configured default.
 *
 * Portals are branded per merchant, and the colour lives in an inline style on a nested div (see
 * `DivWithPrimaryColorVariable`) rather than on `:root` — so the caller passes the values it has read
 * from inside that scope, and this never guesses them off the document root.
 *
 * Returns a teardown function. Two observers are needed: the CMP mounts its host asynchronously, so
 * {@link whenCmpShadowRoot} waits for it rather than polling; and the CMP appends stylesheets whenever it
 * re-renders (a language change, reopening the settings layer), so the second keeps this declaration
 * last, which is what makes it win at equal specificity.
 */
export const applyUsercentricsPrimaryColor = (primary: string, onPrimary: string): (() => void) | undefined => {
	if (typeof document === 'undefined' || !primary) return;
	const css = cmpThemeCss(primary, onPrimary || '#ffffff');

	const write = (shadow: ShadowRoot): void => {
		const existing = shadow.querySelector(`#${CMP_THEME_STYLE_ID}`);
		const style = existing instanceof HTMLStyleElement ? existing : document.createElement('style');
		style.id = CMP_THEME_STYLE_ID;
		if (style.textContent !== css) style.textContent = css;
		// Re-appending only when it is not already last is what stops this from re-triggering itself.
		const styles = [...shadow.querySelectorAll('style')];
		if (styles.at(-1) !== style) shadow.append(style);
	};

	return whenCmpShadowRoot((shadow) => {
		write(shadow);
		const shadowObserver = new MutationObserver(() => write(shadow));
		shadowObserver.observe(shadow, { childList: true });
		return () => shadowObserver.disconnect();
	});
};

/**
 * Opens the CMP's settings layer, so a donor can revisit a choice they already made.
 *
 * A plain function rather than the `<a href="#" onClick="UC_UI.showSecondLayer()">` from the CMP's own
 * docs: that markup only works as an inline handler, which React does not support, and an anchor to `#`
 * would push a history entry and jump focus for what is not a navigation. See `PrivacySettingsLink`.
 */
export const openUsercentricsPrivacySettings = (): void => {
	// Prefer the v3 surface, fall back to the compatibility one, so this keeps working whichever the
	// loaded CMP build exposes.
	const open = window.__ucCmp
		? () => window.__ucCmp?.showSecondLayer()
		: window.UC_UI
			? () => window.UC_UI?.showSecondLayer()
			: undefined;
	if (!open) {
		// Reachable when the CMP is still loading or was blocked outright (an extension, an offline
		// visitor). Nothing to recover, but silence would make a dead control impossible to diagnose.
		console.error(new Error('Usercentrics privacy settings requested before the CMP was available'));
		return;
	}
	void Promise.resolve(open()).catch((error: unknown) => {
		console.error(new Error(`Usercentrics failed to open the privacy settings layer: ${String(error)}`));
	});
};
